/**
 * The marketing pages' content model. Every visible word lives in one of the content modules beside this file;
 * components only lay it out. The copy is Erin's, verbatim: change a word here, never in a component.
 */

/** A block of copy. `pending` names an open decision ("decision 8") and is never rendered as text. */
export interface Block {
  text: string;
  pending?: string;
}

/** Plain copy, or a block that carries a pending marker. */
export type Copy = string | Block;

/**
 * A value still to be supplied. With a value (from build-time env) it renders; without one it shows a visible TODO in
 * development and is left out of production builds.
 */
export interface Placeholder {
  todo: string;
  value?: string;
}

export interface Cta {
  label: string;
  href: string;
}

/** A list item that starts with a bold lead-in: "In your chat assistant. | Connect Claude..." */
export interface LeadItem {
  lead: string;
  body: Copy;
  link?: Cta;
}

export interface Card {
  title: string;
  body: string;
}

/** A card that ends with a text link. */
export interface LinkCard extends Card {
  link: Cta;
}

/** A question with its answer, shown as a row (not an accordion). */
export interface QaRow {
  question: string;
  answer: string;
}

export interface PageMeta {
  path: '/' | '/reps' | '/fit-check' | '/hubspot-app' | '/support' | '/security';
  /** The name HubSpot's form context and the OG image use. */
  name: string;
  title: string;
  description: string;
  /** The OG image, under /og/. */
  ogImage: string;
}

export const textOf = (c: Copy): string => (typeof c === 'string' ? c : c.text);
export const pendingOf = (c: Copy): string | undefined => (typeof c === 'string' ? undefined : c.pending);

/** A block of a reference page (the HubSpot app, support and security pages). */
export type DocBlock =
  | { kind: 'p'; text: string }
  | { kind: 'h3'; text: string }
  | { kind: 'ul'; items: string[] }
  | { kind: 'ol'; items: string[] }
  | { kind: 'links'; items: Cta[] }
  | { kind: 'table'; caption: string; columns: string[]; rows: string[][] };

export interface DocSection {
  /** The heading's id, and the section's anchor. */
  id: string;
  headline: string;
  blocks: DocBlock[];
}

/** A reference page: a hero, then sections of paragraphs, lists and tables. */
export interface DocPageContent {
  meta: PageMeta;
  hero: { eyebrow: string; headline: string; subhead: string };
  sections: DocSection[];
}
